// @ts-check

import {
  BarChart3,
  Box,
  Briefcase,
  Building2,
  CalendarDays,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  Folder,
  Globe,
  Handshake,
  Home,
  Instagram,
  Kanban,
  Layers,
  LayoutGrid,
  ListTodo,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  MessageSquare,
  Package,
  Receipt,
  RefreshCw,
  RotateCcw,
  Ruler,
  Send,
  Settings,
  ShoppingCart,
  Sparkles,
  Star,
  Tag,
  TrendingUp,
  Truck,
  UserCheck,
  Users,
  UsersRound,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import AdminAlertBanner from '../components/AdminAlertBanner';
import AssistantLauncher from '../components/assistant/AssistantLauncher';
import ThemeToggle from '../components/ui/ThemeToggle';
import WhatsAppNavBadge from '../components/whatsapp/WhatsAppNavBadge';
import { UnsavedChangesGuardMount, UnsavedChangesProvider } from '../context/UnsavedChangesContext';
import { WhatsAppUnreadProvider } from '../context/WhatsAppUnreadContext';
import adminAuthService from '../services/adminAuthService';

/** True below the `md` breakpoint (768px), where the sidebar becomes an off-canvas drawer. */
const useIsMobile = () => {
  const query = '(max-width: 767px)';
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

const AdminLayoutShell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMobile = useIsMobile();
  // The mobile drawer always shows full labels; the collapsed icon rail is desktop-only.
  const showLabels = isMobile || sidebarOpen;
  const [expandedSections, setExpandedSections] = useState([]);
  const adminUser = adminAuthService.getCurrentUser();

  // Close the drawer after navigating.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    adminAuthService.logout();
    navigate('/login');
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) =>
      prev.includes(sectionKey) ? prev.filter((key) => key !== sectionKey) : [...prev, sectionKey],
    );
  };

  const navStructure = [
    {
      type: 'single',
      path: '/dashboard',
      icon: Home,
      label: 'Dashboard',
    },
    {
      type: 'section',
      key: 'catalog',
      icon: Package,
      label: 'Catalog',
      items: [
        { path: '/products', icon: Package, label: 'Products' },
        { path: '/collections', icon: Folder, label: 'Collections' },
        { path: '/inventory', icon: Box, label: 'Inventory' },
      ],
    },
    {
      type: 'single',
      path: '/orders',
      icon: ShoppingCart,
      label: 'Orders',
    },
    {
      type: 'single',
      path: '/sales-events',
      icon: CalendarDays,
      label: 'Sales Events',
    },
    {
      type: 'single',
      path: '/invoices',
      icon: FileText,
      label: 'Invoices',
    },
    // The wholesale tracker is Super Admin only (the API enforces it too).
    ...(adminUser?.role === 'super_admin'
      ? [{ type: 'single', path: '/wholesale', icon: Handshake, label: 'Wholesale', matchPrefix: true }]
      : []),
    {
      type: 'single',
      path: '/billing',
      icon: Receipt,
      label: 'Billing',
    },
    {
      type: 'single',
      path: '/shipments',
      icon: Send,
      label: 'Shipments',
    },
    {
      type: 'single',
      path: '/shipping-partners',
      icon: Truck,
      label: 'Shipping Partners',
    },
    {
      type: 'single',
      path: '/customers',
      icon: Users,
      label: 'Customers',
    },
    {
      type: 'single',
      path: '/whatsapp',
      icon: MessageCircle,
      label: 'WhatsApp',
    },
    {
      type: 'single',
      path: '/analytics',
      icon: TrendingUp,
      label: 'Analytics',
    },
    {
      type: 'single',
      path: '/seo',
      icon: Globe,
      label: 'SEO',
    },
    {
      type: 'single',
      path: '/reviews',
      icon: Star,
      label: 'Reviews',
    },
    {
      type: 'single',
      path: '/live-map',
      icon: Globe,
      label: 'Live Map',
    },
    {
      type: 'section',
      key: 'tasks',
      icon: CheckSquare,
      label: 'Tasks',
      items: [
        { path: '/tasks', icon: ListTodo, label: 'All Tasks' },
        { path: '/tasks/kanban', icon: Kanban, label: 'Kanban Board' },
        { path: '/tasks/my-tasks', icon: CheckSquare, label: 'My Tasks' },
        { path: '/tasks/workload', icon: UsersRound, label: 'Team Workload' },
      ],
    },
    {
      type: 'section',
      key: 'atelier',
      icon: Building2,
      label: 'Atelier (B2B)',
      items: [
        { path: '/atelier/crm', icon: TrendingUp, label: 'Outreach CRM' },
        { path: '/atelier/projects', icon: Briefcase, label: 'Projects' },
        { path: '/atelier/accounts', icon: Users, label: 'Trade Accounts' },
        { path: '/atelier/settings', icon: Settings, label: 'Atelier Settings' },
      ],
    },
    {
      type: 'section',
      key: 'marketing',
      icon: Instagram,
      label: 'Marketing',
      items: [
        { path: '/marketing/comments', icon: MessageSquare, label: 'Comments' },
        { path: '/marketing/posts', icon: BarChart3, label: 'Posts' },
        { path: '/influencers', icon: Users, label: 'Influencers' },
        { path: '/marketing/dresses', icon: Package, label: 'Dress Tracking' },
        { path: '/marketing/feed', icon: LayoutGrid, label: 'Feed Curation' },
        { path: '/marketing/showcase', icon: Sparkles, label: 'Showcase' },
      ],
    },
    {
      type: 'section',
      key: 'settings',
      icon: Settings,
      label: 'Settings',
      items: [
        { path: '/settings', icon: Settings, label: 'Site Settings' },
        { path: '/settings/catalog', icon: Tag, label: 'Catalog Settings' },
        { path: '/settings/pricing', icon: DollarSign, label: 'Pricing Config' },
        { path: '/settings/taxonomy', icon: LayoutGrid, label: 'Occasions, Moods & Places' },
        { path: '/settings/size-guides', icon: Ruler, label: 'Size Guides' },
        { path: '/discounts', icon: Tag, label: 'Discounts' },
        { path: '/email-templates', icon: Mail, label: 'Email Templates' },
        { path: '/user-roles', icon: Users, label: 'User Roles' },
        { path: '/site-content', icon: FileText, label: 'Site Content' },
        { path: '/contact-submissions', icon: MessageSquare, label: 'Contact Messages' },
      ],
    },
    {
      type: 'section',
      key: 'business',
      icon: Briefcase,
      label: 'Business',
      items: [
        { path: '/vendors', icon: Building2, label: 'Vendors' },
        { path: '/vendors/orders', icon: Truck, label: 'Vendor Orders' },
        { path: '/materials', icon: Layers, label: 'Materials' },
        { path: '/expenses', icon: Receipt, label: 'Expenses' },
        { path: '/expense-tracker', icon: DollarSign, label: 'Expense Tracker (Old)' },
        { path: '/job-tracker', icon: Briefcase, label: 'Job Tracker' },
        { path: '/hr-tracker', icon: UserCheck, label: 'HR Tracker' },
      ],
    },
    {
      type: 'single',
      path: '/recovery',
      icon: RotateCcw,
      label: 'Recovery',
    },
    {
      type: 'section',
      key: 'developer',
      icon: RefreshCw,
      label: 'Developer',
      items: [{ path: '/developer/staging-sync', icon: RefreshCw, label: 'Staging Sync' }],
    },
  ];

  return (
    <div className="min-h-screen flex bg-muted/50">
      {/* Mobile drawer backdrop */}
      {isMobile && mobileNavOpen && (
        <div className="fixed inset-0 z-30 bg-black/50" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside
        className={`${
          isMobile ? `w-64 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}` : sidebarOpen ? 'w-64' : 'w-20'
        } bg-card border-r border-border transition-all duration-300 flex flex-col fixed h-dvh z-40`}
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-border flex-shrink-0">
          {showLabels && (
            <h1
              className="text-2xl font-serif"
              style={{ fontSize: '25px', letterSpacing: '0.454em', fontWeight: '700' }}
            >
              KAAY
            </h1>
          )}
          <button
            onClick={() => (isMobile ? setMobileNavOpen(false) : setSidebarOpen(!sidebarOpen))}
            className="p-2 hover:bg-accent rounded-lg"
            aria-label={isMobile ? 'Close menu' : sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {showLabels ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="flex-1 py-6 overflow-y-auto flex-shrink overflow-x-hidden">
          {navStructure.map((item, index) => {
            if (item.type === 'single') {
              const Icon = item.icon;
              const isActive = item.matchPrefix
                ? location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
                : location.pathname === item.path;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`relative mx-3 flex items-center rounded-lg px-3 py-3 text-sm transition-colors ${
                    isActive ? 'bg-primary text-primary-foreground' : 'text-foreground/80 hover:bg-accent'
                  }`}
                >
                  <Icon size={20} />
                  {showLabels && <span className="ml-3">{item.label}</span>}
                  {item.path === '/whatsapp' && <WhatsAppNavBadge collapsed={!showLabels} />}
                </NavLink>
              );
            }

            if (item.type === 'section') {
              const Icon = item.icon;
              const isExpanded = expandedSections.includes(item.key);
              const hasActiveChild = item.items.some((child) => location.pathname === child.path);

              return (
                <div key={item.key}>
                  <button
                    onClick={() => toggleSection(item.key)}
                    className={`mx-3 flex w-[calc(100%-1.5rem)] items-center justify-between rounded-lg px-3 py-3 text-sm transition-colors ${
                      hasActiveChild ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-muted/60'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon size={20} />
                      {showLabels && <span className="ml-3 font-medium tracking-wide">{item.label}</span>}
                    </div>
                    {showLabels && (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                  </button>

                  {isExpanded && showLabels && (
                    <div className="bg-muted/50">
                      {item.items.map((child) => {
                        const ChildIcon = child.icon;
                        const isActive = location.pathname === child.path;

                        return (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            className={`mx-3 flex items-center rounded-lg pl-9 pr-3 py-2.5 text-sm transition-colors ${
                              isActive ? 'bg-primary text-primary-foreground' : 'text-foreground/80 hover:bg-accent'
                            }`}
                          >
                            <ChildIcon size={18} />
                            <span className="ml-3">{child.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
        </nav>

        <div className="border-t border-border p-4 flex-shrink-0">
          {showLabels && adminUser && (
            <div className="mb-3">
              <p className="text-sm font-medium text-foreground">{adminUser.full_name}</p>
              <p className="text-xs text-muted-foreground">{adminUser.email}</p>
              <p className="text-xs text-muted-foreground/70 capitalize mt-1">{adminUser.role}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40 rounded-lg"
          >
            <LogOut size={18} />
            {showLabels && <span className="ml-2">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col min-w-0 ${location.pathname === '/whatsapp' ? 'h-dvh' : ''} ${sidebarOpen ? 'md:ml-64' : 'md:ml-20'} transition-all duration-300`}
      >
        <header className="h-16 bg-card border-b border-border flex items-center justify-between gap-3 px-4 lg:px-6 sticky top-0 z-10">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden -ml-2 p-2.5 hover:bg-accent rounded-lg flex-shrink-0"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <h2 className="text-lg md:text-xl font-serif truncate min-w-0 flex-1">
            {(() => {
              // Find single item match
              const singleItem = navStructure.find((item) => item.type === 'single' && item.path === location.pathname);
              if (singleItem) return singleItem.label;

              // Find nested item match
              const sectionWithMatch = navStructure.find(
                (item) => item.type === 'section' && item.items.some((child) => child.path === location.pathname),
              );
              if (sectionWithMatch) {
                const matchedChild = sectionWithMatch.items.find((child) => child.path === location.pathname);
                return matchedChild?.label || 'Admin Panel';
              }

              return 'Admin Panel';
            })()}
          </h2>
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="hidden lg:block text-sm text-muted-foreground">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
            <AssistantLauncher />
            <ThemeToggle />
          </div>
        </header>

        <main className={`flex-1 min-h-0 overflow-y-auto p-4 lg:p-6`}>
          {/* The Dashboard shows these itself (with extra actions, e.g. "Configure Pricing") - skip here to avoid showing each alert twice. */}
          {location.pathname !== '/dashboard' && <AdminAlertBanner />}
          <UnsavedChangesProvider>
            <Outlet />
            <UnsavedChangesGuardMount />
          </UnsavedChangesProvider>
        </main>
      </div>
    </div>
  );
};

/** Mounts the WhatsApp unread poller for every admin page; toast / notification clicks open that chat. */
const AdminLayout = () => {
  const navigate = useNavigate();
  return (
    <WhatsAppUnreadProvider
      onOpenChat={(phone) => navigate(`/whatsapp?tab=inbox&phone=${encodeURIComponent(phone)}&t=${Date.now()}`)}
    >
      <AdminLayoutShell />
    </WhatsAppUnreadProvider>
  );
};

export default AdminLayout;
